import { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import debounce from "lodash.debounce";
import { format, addDays, parseISO, isValid } from "date-fns";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { getDefaultClassNames } from "react-day-picker";

// --- Images ---
import logo from "../images/Aombre-round-logo.png";
import banner from "../images/location-page-bg.webp"; // Default fallback
import toronto from "../images/TORONTO.webp";
import ottawa from "../images/Ottawa-m.webp";
import orlando from "../images/orlando.webp";
import denver from "../images/denver.webp";
import atlanta from "../images/atlanta.webp";
import vancouver from "../images/Vancouver.webp";
import calgary from "../images/Calgary.webp";
import chicago from "../images/Chicago.webp";
import montreal from "../images/Montreal.webp";

import {
  Plane,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  ArrowBigLeft,
  ArrowLeft,
  ChevronDown,
} from "lucide-react";
import { Phone, Mail } from "lucide-react";

// --- 1. IMAGE MAPPING CONFIGURATION ---
// Keys should be lowercase to make matching easier
const cityImages = {
  toronto: toronto,
  ottawa: ottawa,
  orlando: orlando,
  denver: denver,
  atlanta: atlanta,
  vancouver: vancouver,
  calgary: calgary,
  chicago: chicago,
  montreal: montreal,
  montréal: montreal, // Handle accent from API
};

// --- Custom Hook to track window size ---
const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowWidth;
};

// CONTACT FORMAT HELPER FUTN
const formatPhoneNumber = (phoneNumberString) => {
  if (!phoneNumberString) return "";
  const cleaned = ("" + phoneNumberString).replace(/\D/g, "");

  // Case 1: North American Number (11 digits starting with 1)
  const matchNA = cleaned.match(/^(1)(\d{3})(\d{3})(\d{4})$/);
  if (matchNA) {
    return `+${matchNA[1]} (${matchNA[2]}) ${matchNA[3]}-${matchNA[4]}`;
  }

  // Case 2: Standard 10 digit US/Canada number (missing the 1)
  const match10 = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match10) {
    return `+1 (${match10[1]}) ${match10[2]}-${match10[3]}`;
  }

  // Case 3: International/Unknown (fallback)
  return `+${cleaned}`;
};

// --- Helper Functions ---
const convertTo24Hour = (time12) => {
  if (!time12) return "00:00";
  const [time, modifier] = time12.split(" ");
  let [hours, minutes] = time.split(":");
  if (hours === "12") hours = "00";
  if (modifier === "PM") hours = parseInt(hours, 10) + 12;
  return `${String(hours).padStart(2, "0")}:${minutes}`;
};
const convertTo12Hour = (time24) => {
  if (!time24) return "12:00 AM";
  const [hours, minutes] = time24.split(":").map(Number);
  const modifier = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  return `${String(formattedHours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )} ${modifier}`;
};
const generateTimeOptions = () => {
  const times = [];
  for (let i = 0; i < 24 * 60; i += 30) {
    const date = new Date(2000, 0, 1, Math.floor(i / 60), i % 60);
    times.push(format(date, "hh:mm a"));
  }
  return times;
};
const combineDateAndTime = (date, time12) => {
  if (!date || !time12) return "";
  return `${format(date, "yyyy-MM-dd")}T${convertTo24Hour(time12)}`;
};

function LocationDetails() {
  const defaultClassNames = getDefaultClassNames();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const [locationData, setLocationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768; // md breakpoint

  // --- Refs ---
  const datePickerRef = useRef(null);
  const pickupLocationRef = useRef(null);
  const dropoffLocationRef = useRef(null);

  // --- State ---
  const [sameLocation, setSameLocation] = useState(true);
  const [pickupQuery, setPickupQuery] = useState("");
  const [dropoffQuery, setDropoffQuery] = useState("");
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDropoffSuggestions, setShowDropoffSuggestions] = useState(false);
  const [pickupLocationObj, setPickupLocationObj] = useState(null);
  const [dropoffLocationObj, setDropoffLocationObj] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: addDays(new Date(), 1),
    to: addDays(new Date(), 3),
  });
  const [pickupTime, setPickupTime] = useState("11:30 AM");
  const [dropoffTime, setDropoffTime] = useState("11:30 AM");
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const timeOptions = generateTimeOptions();
  const [pickupError, setPickupError] = useState("");
  const [dropoffError, setDropoffError] = useState("");
  const [dateTimeError, setDateTimeError] = useState("");
  const [policies, setPolicies] = useState([]);
  const [openPolicyIndex, setOpenPolicyIndex] = useState(null);

  // --- Data Fetching ---
  const fetchLocations = async (query, setSuggestions) => {
    if (query.length < 2) return setSuggestions([]);
    try {
      const res = await axios.get(
        `https://backend.aombrerental.com/api/get_country_city_airport_name`
      );
      const filtered = res.data.filter((loc) =>
        `${loc.city} ${loc.airport_name} ${loc.country_name} ${loc.airport_code}`
          .toLowerCase()
          .includes(query.toLowerCase())
      );
      setSuggestions(filtered);
    } catch (error) {
      console.error("Error fetching location suggestions:", error);
    }
  };
  const debouncedFetchPickup = useCallback(
    debounce((query) => fetchLocations(query, setPickupSuggestions), 300),
    []
  );
  const debouncedFetchDropoff = useCallback(
    debounce((query) => fetchLocations(query, setDropoffSuggestions), 300),
    []
  );
  const handleSelectPickup = (loc) => {
    setPickupQuery(
      `${loc.city} - ${loc.airport_name} (${loc.airport_code}), ${loc.country_name}`
    );
    setPickupLocationObj(loc);
    setShowPickupSuggestions(false);
  };
  const handleSelectDropoff = (loc) => {
    setDropoffQuery(
      `${loc.city} - ${loc.airport_name} (${loc.airport_code}), ${loc.country_name}`
    );
    setDropoffLocationObj(loc);
    setShowDropoffSuggestions(false);
  };

  //
  const togglePolicy = (index) => {
    setOpenPolicyIndex(openPolicyIndex === index ? null : index);
  };
  // --- Effects ---
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (code) {
      axios
        .get(
          "https://backend.aombrerental.com/api/routes_website_api_location_details"
        )
        .then((res) => {
          const data = res.data;
          let found = null;
          for (const currency in data) {
            if (data[currency] && Array.isArray(data[currency])) {
              found = data[currency].find((loc) => loc.short_name === code);
              if (found) break;
            }
          }
          if (found) {
            setLocationData(found);
            // Auto-fill pickup
            const addressParts = found.address.split(",");
            const city = addressParts[1]?.trim() || found.f_name;
            setPickupQuery(
              `${city} - ${found.f_name} (${found.short_name}), ${found.country}`
            );
            setPickupLocationObj({
              airport_code: found.short_name,
              city: city,
              country_name: found.country,
              airport_name: found.f_name,
            });
          } else {
            setError("Location not found");
          }
        })
        .catch((err) => {
          console.error(err);
          setError("Failed to fetch location details");
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    if (code) {
      const fetchPolicies = async () => {
        try {
          const response = await axios.get(
            `https://backend.aombrerental.com/api/new_routes_website_api_all_policies/${code}`
          );
          // Filter out any empty policies to avoid displaying them
          const validPolicies = response.data.filter(
            (p) => p.name && p.content
          );
          setPolicies(validPolicies);
        } catch (error) {
          console.error("Failed to fetch policies:", error);
        }
      };
      fetchPolicies();
    }
  }, [code]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      )
        setDatePickerOpen(false);
      if (
        pickupLocationRef.current &&
        !pickupLocationRef.current.contains(event.target)
      )
        setShowPickupSuggestions(false);
      if (
        dropoffLocationRef.current &&
        !dropoffLocationRef.current.contains(event.target)
      )
        setShowDropoffSuggestions(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Form Submission ---
  const handleSubmit = (e) => {
    e.preventDefault();
    let hasErrors = false;
    setPickupError("");
    setDropoffError("");
    setDateTimeError("");
    if (!pickupLocationObj) {
      setPickupError("Please select a valid pickup location.");
      hasErrors = true;
    }
    if (!sameLocation && !dropoffLocationObj) {
      setDropoffError("Please select a valid drop-off location.");
      hasErrors = true;
    }
    const finalPickupDateTime = new Date(
      combineDateAndTime(dateRange.from, pickupTime)
    );
    const finalDropoffDateTime = new Date(
      combineDateAndTime(dateRange.to, dropoffTime)
    );
    if (
      !dateRange.from ||
      !dateRange.to ||
      finalDropoffDateTime <= finalPickupDateTime
    ) {
      setDateTimeError("Drop-off date and time must be after pickup.");
      hasErrors = true;
    }
    if (hasErrors) return;
    const dropLocObj = sameLocation ? pickupLocationObj : dropoffLocationObj;
    const dropQuery = sameLocation ? pickupQuery : dropoffQuery;
    const params = new URLSearchParams({
      pickup_date: combineDateAndTime(dateRange.from, pickupTime),
      pickup_location: pickupQuery,
      pickup_airport_code: pickupLocationObj.airport_code,
      pickup_city: pickupLocationObj.city,
      pickup_country: pickupLocationObj.country_name,
      pickup_airport_name: pickupLocationObj.airport_name,
      dropoff_date: combineDateAndTime(dateRange.to, dropoffTime),
      dropoff_location: dropQuery,
      dropoff_airport_code: dropLocObj.airport_code,
      dropoff_city: dropLocObj.city,
      dropoff_country: dropLocObj.country_name,
      dropoff_airport_name: dropLocObj.airport_name,
      same_location: sameLocation,
    });
    navigate(`/results?${params.toString()}`);
  };

  // week day fromat
  const dayOrder = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  // --- 2. GET DYNAMIC BACKGROUND IMAGE ---
  const getBackgroundImage = () => {
    // If no data yet, use default
    if (!locationData) return banner;

    // Create a search string to find the matching image
    // We check 'city' first, then 'address', then 'f_name' (airport name)
    const searchString = `
      ${locationData.city || ""} 
      ${locationData.address || ""} 
      ${locationData.f_name || ""}
    `.toLowerCase();

    // Find which key from cityImages exists in the searchString
    const matchedCityKey = Object.keys(cityImages).find((key) =>
      searchString.includes(key)
    );

    return matchedCityKey ? cityImages[matchedCityKey] : banner;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <img
          src={logo}
          alt="Company Logo"
          className="w-48 h-48 rounded-full animate-spin-slow"
        />
        <p className="mt-4 text-gray-700 font-semibold">Loading...</p>
      </div>
    );
  }
  return (
    <div>
      {/* Hero Section -Form */}
      <div className="relative mt-8 md:mt-[70px] py-16 sm:py-20 md:py-24">
        {" "}
        <img
          src={getBackgroundImage()} // <--- 3. UPDATED SRC HERE
          alt="Location Banner"
          className="absolute inset-0 w-full h-full object-cover -z-10 transition-opacity duration-500"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40" />
        <div className="flex flex-col items-center justify-center text-center px-4">
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 drop-shadow-lg">
              {locationData
                ? `Book from ${locationData.full_name}`
                : "Find Your Perfect Ride"}
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-white max-w-lg mx-auto drop-shadow-md">
              Hassle-free Car Rentals with AOMBRE
            </p>
          </div>
          {/* Form */}
          <div className="w-full max-w-screen-xl mx-auto pb-12">
            <div className="bg-white p-4 sm:p-8 rounded-xl border border-orange-400/50 shadow-lg w-full relative">
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col lg:flex-row lg:items-end gap-4 relative">
                  <div
                    ref={pickupLocationRef}
                    className={`w-full ${
                      sameLocation ? "lg:flex-[3]" : "lg:flex-[2]"
                    }`}
                  >
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                      <input
                        autoComplete="off"
                        id="pickup-location"
                        value={pickupQuery}
                        onChange={(e) => {
                          setPickupQuery(e.target.value);
                          setPickupLocationObj(null);
                          setShowPickupSuggestions(true);
                          debouncedFetchPickup(e.target.value);
                        }}
                        onFocus={() => {
                          setShowPickupSuggestions(true);
                          setPickupError("");
                        }}
                        placeholder=" "
                        className="peer w-full border border-gray-300 rounded-md pl-10 pr-4 pt-5 pb-2 text-base h-12 sm:h-[60px] focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                      />
                      <label
                        htmlFor="pickup-location"
                        className="absolute text-gray-500 left-10 top-1/2 -translate-y-1/2 transition-all duration-300 peer-focus:top-2 peer-focus:text-xs peer-focus:-translate-y-0 peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:-translate-y-0 pointer-events-none"
                      >
                        Pickup Location
                      </label>
                      {showPickupSuggestions &&
                        pickupSuggestions.length > 0 && (
                          <ul className="absolute z-30 w-full bg-white border rounded shadow-lg max-h-60 overflow-y-auto mt-1">
                            {pickupSuggestions.map((loc) => (
                              <li
                                key={loc.airport_code}
                                onClick={() => handleSelectPickup(loc)}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-sm"
                              >
                                <Plane className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                <span>
                                  {loc.city} - {loc.airport_name} (
                                  {loc.airport_code}), {loc.country_name}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>

                    {pickupError && (
                      <p className="text-red-500 text-xs mt-1 text-left pl-1">
                        {pickupError}
                      </p>
                    )}
                  </div>
                  {!sameLocation && (
                    <div
                      ref={dropoffLocationRef}
                      className="w-full lg:flex-[2]"
                    >
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                        <input
                          autoComplete="off"
                          id="dropoff-location"
                          value={dropoffQuery}
                          onChange={(e) => {
                            setDropoffQuery(e.target.value);
                            setDropoffLocationObj(null);
                            setShowDropoffSuggestions(true);
                            debouncedFetchDropoff(e.target.value);
                          }}
                          onFocus={() => {
                            setShowDropoffSuggestions(true);
                            setDropoffError("");
                          }}
                          placeholder=" "
                          className="peer w-full border border-gray-300 rounded-md pl-10 pr-4 pt-5 pb-2 text-base h-12 sm:h-[60px] focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                        />
                        <label
                          htmlFor="dropoff-location"
                          className="absolute text-gray-500 left-10 top-1/2 -translate-y-1/2 transition-all duration-300 peer-focus:top-2 peer-focus:text-xs peer-focus:-translate-y-0 peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:-translate-y-0 pointer-events-none"
                        >
                          Drop-off Location
                        </label>
                        {showDropoffSuggestions &&
                          dropoffSuggestions.length > 0 && (
                            <ul className="absolute z-30 w-full bg-white border rounded shadow-lg max-h-60 overflow-y-auto mt-1">
                              {dropoffSuggestions.map((loc) => (
                                <li
                                  key={loc.airport_code}
                                  onClick={() => handleSelectDropoff(loc)}
                                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-sm"
                                >
                                  <Plane className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                  <span>
                                    {loc.city} - {loc.airport_name} (
                                    {loc.airport_code}), {loc.country_name}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                      </div>

                      {dropoffError && (
                        <p className="text-red-500 text-xs mt-1 text-left pl-1">
                          {dropoffError}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="w-full lg:flex-[2] flex flex-col sm:flex-row gap-3 relative">
                    <div className="flex-1">
                      <div className="relative">
                        <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                        <button
                          type="button"
                          onClick={() => setDatePickerOpen(true)}
                          className="w-full text-left border border-gray-300 rounded-md pl-10 pr-4 pt-5 pb-2 text-base h-12 sm:h-[60px] focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none truncate"
                        >
                          {dateRange?.from
                            ? format(dateRange.from, "LLL dd, yyyy")
                            : ""}
                        </button>
                        <label className="absolute left-10 top-2 text-xs text-gray-500 pointer-events-none">
                          Pickup Date
                        </label>
                      </div>
                    </div>
                    <div className="w-full sm:w-32">
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                        <select
                          value={pickupTime}
                          onChange={(e) => setPickupTime(e.target.value)}
                          className="w-full border border-gray-300 rounded-md pl-10 pr-4 pt-5 pb-2 text-base h-12 sm:h-[60px] focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none appearance-none bg-white"
                        >
                          {timeOptions.map((time) => (
                            <option key={`pickup-${time}`} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>
                        <label className="absolute left-10 top-2 text-xs text-gray-500 pointer-events-none">
                          Time
                        </label>
                      </div>
                    </div>
                    {isDatePickerOpen && (
                      <div
                        className="absolute z-20 top-full left-0 mt-2 bg-white border rounded-md shadow-lg"
                        ref={datePickerRef}
                      >
                        <DayPicker
                          mode="range"
                          selected={dateRange}
                          onSelect={setDateRange}
                          numberOfMonths={isMobile ? 1 : 2}
                          disabled={{ before: new Date() }}
                          classNames={{
                            months: "flex flex-col sm:flex-row gap-4",
                            root: `${defaultClassNames.root} shadow-lg p-3 sm:p-5`,
                            day: `group rounded-full w-8 h-8 sm:w-10 sm:h-10 ${defaultClassNames.day}`,
                            caption_label: "text-sm sm:text-base",
                          }}
                          components={{
                            DayButton: (props) => {
                              const { date, ...buttonProps } = props;
                              return (
                                <button
                                  {...buttonProps}
                                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full transition-colors group-hover:bg-gray-100 group-aria-selected:bg-[#ca8032] group-aria-selected:text-white"
                                />
                              );
                            },
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="w-full lg:flex-[2] flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <div className="relative">
                        <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                        <button
                          type="button"
                          onClick={() => setDatePickerOpen(true)}
                          className="w-full text-left border border-gray-300 rounded-md pl-10 pr-4 pt-5 pb-2 text-base h-12 sm:h-[60px] focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none truncate"
                        >
                          {dateRange?.to
                            ? format(dateRange.to, "LLL dd, yyyy")
                            : ""}
                        </button>
                        <label className="absolute left-10 top-2 text-xs text-gray-500 pointer-events-none">
                          Drop-off Date
                        </label>
                      </div>
                    </div>
                    <div className="w-full sm:w-32">
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                        <select
                          value={dropoffTime}
                          onChange={(e) => setDropoffTime(e.target.value)}
                          className="w-full border border-gray-300 rounded-md pl-10 pr-4 pt-5 pb-2 text-base h-12 sm:h-[60px] focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none appearance-none bg-white"
                        >
                          {timeOptions.map((time) => (
                            <option key={`dropoff-${time}`} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>
                        <label className="absolute left-10 top-2 text-xs text-gray-500 pointer-events-none">
                          Time
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="w-full lg:w-auto">
                    <button
                      type="submit"
                      className="btn px-6 text-white rounded-lg bg-[#ca8032] w-full h-[60px] text-lg hover:bg-[#b5702a] transition-colors font-semibold flex items-center justify-center"
                    >
                      Search
                    </button>
                  </div>
                </div>
                {dateTimeError && (
                  <p className="text-red-500 text-sm mt-2 font-medium">
                    {dateTimeError}
                  </p>
                )}
                <div className="mt-4 flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-700">
                    Return to same location
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sameLocation}
                      onChange={() => setSameLocation(!sameLocation)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-[#ca8032]"></div>
                    <div className="absolute top-[2px] left-[2px] bg-white border-gray-300 border rounded-full h-5 w-5 transition-all peer-checked:translate-x-full"></div>
                  </label>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* Explore More Locations Button */}
      <div className="max-w-7xl mx-auto mt-6 px-6">
        <button
          onClick={() => navigate("/locations")}
          className="btn flex items-center gap-2 font-semibold  px-6 rounded-lg"
        >
          <ArrowLeft />
          <span>Explore More Locations</span>
        </button>
      </div>

      {/* Location Details */}
      {locationData && (
        <div className="p-6 max-w-7xl mx-auto mt-2">
          <div className="bg-white rounded-2xl shadow-md p-6">
            {/* Main Title */}
            <h2 className="text-3xl font-bold text-[#006039] mb-6">
              {locationData.full_name} Details
            </h2>

            {/* Wrapper for consistent vertical spacing between sections */}
            <div className="flex flex-col gap-6">
              {/* Address Section */}
              <div>
                <h3 className="text-xl font-semibold text-[#ca8032] mb-2">
                  Address
                </h3>
                <p className="text-gray-700">{locationData.address}</p>
              </div>

              {/* --- FIXED: Contact and Email in separate, responsive containers --- */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contact Box */}
                <div className="bg-[#FFF7F0] p-5 rounded-lg border border-[#ca8032]/20">
                  <div className="flex items-center mb-2">
                    <Phone className="h-5 w-5 text-[#ca8032] mr-2" />
                    <h3 className="text-lg font-semibold text-[#ca8032]">
                      Contact
                    </h3>
                  </div>
                  {/* Indented text for alignment with the title/icon */}
                  <p className="text-gray-700 md:pl-7">
                    {formatPhoneNumber(locationData.number)}
                  </p>
                </div>

                {/* Email Box - Commented out as per your original code */}
                {/* <div className="bg-[#FFF7F0] p-5 rounded-lg border border-[#ca8032]/20">
                  <div className="flex items-center mb-2">
                    <Mail className="h-5 w-5 text-[#ca8032] mr-2" />
                    <h3 className="text-lg font-semibold text-[#ca8032]">
                      Email
                    </h3>
                  </div>
                  <div className="md:pl-7">
                    <p className="text-gray-700">{locationData.email1}</p>
                    {locationData.email2 && (
                      <p className="text-gray-700 mt-1">
                        {locationData.email2}
                      </p>
                    )}
                  </div>
                </div> */}
              </div>

              {/* --- Operational Hours and Map Section --- */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
                {/* Left: Operational Hours */}
                <div className="w-full flex flex-col">
                  <h3 className="text-2xl font-bold text-[#ca8032] mb-4 border-b-2 border-[#ca8032]/30 pb-2">
                    Operational Hours
                  </h3>

                  <div className="bg-gradient-to-br from-[#FFF7F0] to-white border border-[#ca8032]/20 rounded-2xl p-6 shadow-sm flex-1">
                    <div className="space-y-3 h-full flex flex-col justify-between">
                      {/* 2. Map over the specific order array, NOT the object entries */}
                      {dayOrder.map((day) => {
                        // 3. Look up the data for this day
                        const times = locationData.timeings[day];

                        // Safety check: If the API didn't send data for this day, skip it
                        if (!times) return null;

                        return (
                          <div
                            key={day}
                            className="flex justify-between items-center bg-white/50 rounded-lg p-3 shadow-inner hover:bg-[#FFE8D1] transition duration-200"
                          >
                            <span className="font-medium text-gray-800 capitalize">
                              {day}
                            </span>
                            <span className="text-gray-600 text-sm">
                              {times.from} - {times.to}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Right: Map */}
                <div className="w-full flex flex-col ">
                  <h3 className="text-2xl font-bold text-[#ca8032] mb-4 border-b-2 border-[#ca8032]/30 pb-2">
                    Location Map
                  </h3>

                  {/* --- map --- */}
                  <div className="rounded-2xl z-0 overflow-hidden shadow-md border border-[#ca8032]/20 h-64 lg:flex-1">
                    <iframe
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://maps.google.com/maps?q=${locationData.latlng.lat},${locationData.latlng.long}&z=14&output=embed`}
                    ></iframe>
                  </div>
                  {/* --- END --- */}
                </div>
              </div>

              {/* */}
              {policies.length > 0 && (
                <div>
                  <h3 className="text-2xl font-bold text-[#ca8032] mb-4 border-b-2 border-[#ca8032]/30 pb-2">
                    Rental Policies
                  </h3>
                  <div className="space-y-3">
                    {policies.map((policy, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg overflow-hidden transition-all duration-300"
                      >
                        <button
                          onClick={() => togglePolicy(index)}
                          className="w-full flex justify-between items-center p-4 text-left font-semibold text-gray-800 bg-gray-50 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ca8032]"
                        >
                          <span className="flex-1">{policy.name}</span>
                          <ChevronDown
                            className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                              openPolicyIndex === index
                                ? "transform rotate-180"
                                : ""
                            }`}
                          />
                        </button>
                        {openPolicyIndex === index && (
                          <div className="p-4 bg-white border-t border-gray-200">
                            <p className="text-gray-700 whitespace-pre-wrap">
                              {policy.content}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LocationDetails;
